import { get, set } from './sqlite-store';
import { genId } from '../../lib/uuid';
import { offlineLog } from './log-buffer';

const QUEUE_KEY = 'tc-mutation-queue';

export type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type MutationStatus =
  | 'pending'
  | 'in-flight'
  | 'succeeded'
  | 'failed'
  | 'conflict';

export interface QueuedMutation {
  id: string;
  /** Client-generated UUID for idempotent replay */
  clientId: string;
  method: MutationMethod;
  path: string; // e.g., /entities/work_order/abc
  body?: unknown;
  /** Optional entity reference for conflict detection + UI display */
  entityType?: string;
  entityId?: string;
  /** Snapshot of the entity before this mutation (for 3-way merge) */
  baselineVersion?: string; // updatedAt of the entity when the mutation was queued
  /** Snapshot of the user-visible data the mutation intended to apply */
  intendedData?: Record<string, unknown>;
  /** Optional human-friendly label for the queue UI */
  label?: string;
  createdAt: number;
  attemptCount: number;
  lastAttemptAt?: number;
  status: MutationStatus;
  error?: string;
  /** If conflict detected, the fresh server version at replay time */
  serverVersion?: Record<string, unknown>;
  /** Server-issued conflict id for escalation tracking */
  conflictId?: string;
  /** Whether this mutation has been escalated to admin */
  escalated?: boolean;
}

/**
 * Persistent, ordered queue of mutations backed by SQLite (native) or
 * in-memory on web. The queue survives page reloads and app restarts.
 */
export class MutationQueue {
  private subscribers = new Set<() => void>();

  async load(): Promise<QueuedMutation[]> {
    const raw = await get<QueuedMutation[]>(QUEUE_KEY);
    return raw ?? [];
  }

  async enqueue(item: Omit<QueuedMutation, 'id' | 'clientId' | 'createdAt' | 'attemptCount' | 'status'>): Promise<QueuedMutation> {
    const queue = await this.load();
    const next: QueuedMutation = {
      id: genId(),
      clientId: genId(),
      createdAt: Date.now(),
      attemptCount: 0,
      status: 'pending',
      ...item,
    };
    queue.push(next);
    await set(QUEUE_KEY, queue);
    offlineLog.sync(`Enqueued mutation ${next.clientId}: ${next.method} ${next.path}`);
    this.emit();
    return next;
  }

  async update(id: string, patch: Partial<QueuedMutation>): Promise<void> {
    const queue = await this.load();
    const idx = queue.findIndex((m) => m.id === id);
    if (idx < 0) return;
    queue[idx] = { ...queue[idx], ...patch };
    await set(QUEUE_KEY, queue);
    this.emit();
  }

  async remove(id: string): Promise<void> {
    const queue = await this.load();
    await set(
      QUEUE_KEY,
      queue.filter((m) => m.id !== id),
    );
    this.emit();
  }

  async clearCompleted(): Promise<void> {
    const queue = await this.load();
    await set(
      QUEUE_KEY,
      queue.filter((m) => m.status !== 'succeeded'),
    );
    this.emit();
  }

  async pendingCount(): Promise<number> {
    const queue = await this.load();
    return queue.filter((m) => m.status === 'pending' || m.status === 'in-flight')
      .length;
  }

  subscribe(cb: () => void): () => void {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  private emit() {
    this.subscribers.forEach((cb) => cb());
  }
}

export const mutationQueue = new MutationQueue();
